import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, CssBaseline } from '@mui/material';
import MapView from './MapView.jsx';
import TopToolbarNew from './TopToolbar.js';
import PropertySlider from './PropertySlider.jsx';
import PropertyModal from './PropertyModal.jsx';
import { getAuctionFilterOptions, getAuctionMapProperties } from './api.js';
import FormsBottomNavbar from '../Post Module/FormsBottomNavbar.js';

const PRICE_MAX = 5000000000;

const AuctionIndex = () => {
  const savedMapState = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('auctionmap') || 'null');
    } catch {
      return null;
    }
  }, []);

  const [viewport, setViewport] = useState({
    bounds: { south: 6.5, north: 35.5, west: 68, east: 97.5 },
    zoom: savedMapState ? savedMapState.zoom : 5,
  });
  
  const [selectedMainCategories, setSelectedMainCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, PRICE_MAX]);
  const [visibleProperties, setVisibleProperties] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [modalProperty, setModalProperty] = useState(null);
  
  // State for category color map
  const [categoryColorMap, setCategoryColorMap] = useState({});
  
  const [flyTarget, setFlyTarget] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('auctionmap') || 'null');
      const lastViewed = localStorage.getItem('lastViewedPropertyId');
      if (saved && lastViewed) {
        return { lat: saved.lat, lng: saved.lng, zoom: saved.zoom, key: Date.now() };
      }
      if (saved) {
        return { lat: saved.lat, lng: saved.lng, zoom: saved.zoom, key: Date.now() };
      }
    } catch {
      // ignore
    }
    return null;
  });
  
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showAlert, setShowAlert] = useState(null);
  const sliderRef = useRef(null);
  
  const [mainCategoryOptions, setMainCategoryOptions] = useState([]); 
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);

  // ❌ REMOVED - This was clearing lastViewedPropertyId on mount
  /*
  useEffect(() => {
    const lastViewed = localStorage.getItem('lastViewedPropertyId');
    if (lastViewed) {
      localStorage.removeItem('lastViewedPropertyId');
    }
  }, []);
  */

  useEffect(() => {
    const loadOptions = async () => {
      const data = await getAuctionFilterOptions();
      console.log('Filter options loaded:', data);
      if (data.success) {
        setPriceRange([data.priceRange.min, data.priceRange.max]);
        setMainCategoryOptions(data.typeOptions || []);
        setSubCategoryOptions(data.propertyTypeOptions || []);
        
        // Generate color map for property types
        const colorPalette = [
          '#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#E53935',
          '#00BCD4', '#FF5722', '#795548', '#607D8B', '#E91E63',
          '#8BC34A', '#009688', '#FFC107', '#673AB7', '#F44336',
          '#3F51B5', '#CDDC39', '#FFEB3B', '#9E9E9E', '#D32F2F',
          '#7B1FA2', '#C2185B', '#0288D1', '#00796B', '#F57C00',
          '#455A64', '#6A1B9A', '#AD1457', '#00695C', '#E65100'
        ];
        
        const colorMap = {};
        
        // Collect all property types from both main and sub categories
        const allPropertyTypes = [];
        
        // Add main category options (JV/JD, BUILD TO SUIT)
        if (data.typeOptions && data.typeOptions.length) {
          data.typeOptions.forEach(item => {
            const typeValue = typeof item === 'string' ? item : item.value;
            if (typeValue && typeValue !== '') {
              allPropertyTypes.push(typeValue);
            }
          });
        }
        
        // Add property type options (PLOT, LAND, etc.)
        if (data.propertyTypeOptions && data.propertyTypeOptions.length) {
          data.propertyTypeOptions.forEach(item => {
            const typeValue = typeof item === 'string' ? item : item.value;
            if (typeValue && typeValue !== '') {
              allPropertyTypes.push(typeValue);
            }
          });
        }
        
        // Generate color map for unique property types
        const uniqueTypes = [...new Set(allPropertyTypes)];
        uniqueTypes.forEach((type, index) => {
          colorMap[type] = colorPalette[index % colorPalette.length];
        });
        
        // Add default color for any unknown types
        colorMap['default'] = '#E53935';
        
        setCategoryColorMap(colorMap);
        console.log('Category color map generated:', colorMap);
      }
    };
    loadOptions();
  }, []);

  // Create filters object
  const filters = useMemo(() => ({
    type: selectedMainCategories,
    propertyType: selectedSubCategories,
    priceMin: priceRange[0],
    priceMax: priceRange[1],
  }), [selectedMainCategories, selectedSubCategories, priceRange]);

  const handleViewportChange = useCallback((vp) => {
    setViewport(vp);
  }, []);

  const handlePropertiesUpdate = useCallback((props, showAlertFn) => {
    console.log('Properties received:', props.length);
    setVisibleProperties(props);
    
    if (showAlertFn) {
      setShowAlert(() => showAlertFn);
    }
  }, []);

  const handleLocationSelect = useCallback((loc) => {
    setSelectedLocation(loc);
    if (loc) {
      const lat = loc.lat ?? loc.latitude;
      const lng = loc.lng ?? loc.lon ?? loc.longitude;
      if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
        setFlyTarget({ lat: parseFloat(lat), lng: parseFloat(lng), zoom: 13, key: Date.now() });
      }
    }
  }, []);

  const handleClearLocation = useCallback(() => {
    setSelectedLocation(null);
    setFlyTarget({ lat: 20.5937, lng: 78.9629, zoom: 5, key: Date.now() });
  }, []);

const handleMarkerClick = useCallback((p) => {
  console.log('Marker clicked:', p.id);
  setSelectedId(p.id);

  const currentZoom = viewport.zoom || 5;

  const stateToSave = {
    lat: p.lat,
    lng: p.lng,
    zoom: currentZoom,
    timestamp: Date.now(),
  };
  
  localStorage.setItem('auctionmap', JSON.stringify(stateToSave));
  localStorage.setItem('lastViewedPropertyId', p.id.toString());

  setFlyTarget({
    lat: p.lat,
    lng: p.lng,
    zoom: currentZoom,
    key: Date.now()
  });

  setTimeout(() => {
    if (sliderRef.current) {
      const index = sliderRef.current.getCardIndex?.(p.id);
      if (index !== undefined && index !== -1) {
        sliderRef.current.scrollToCard(index);
      }
    }
  }, 200);
}, [viewport.zoom]);

const handleCardSelect = useCallback((p) => {
  setSelectedId(p.id);

  // Keep current zoom level
  const currentZoom = viewport.zoom || 5;

  localStorage.setItem('auctionmap', JSON.stringify({
    lat: p.lat,
    lng: p.lng,
    zoom: currentZoom,
    timestamp: Date.now(),
  }));
  localStorage.setItem('lastViewedPropertyId', p.id.toString());

  setFlyTarget({ 
    lat: p.lat, 
    lng: p.lng, 
    zoom: currentZoom, 
    key: Date.now() 
  });
}, [viewport.zoom]);

  const handleCardInView = useCallback((p) => {
    setSelectedId(p.id);
  }, []);

  const handlePriceRangeChange = useCallback((newRange) => {
    console.log('Price range changed:', newRange);
    setPriceRange(newRange);
  }, []);


useEffect(() => {
  const lastViewedId = localStorage.getItem('lastViewedPropertyId');
  if (lastViewedId && visibleProperties.length > 0) {
    const id = parseInt(lastViewedId);
    const property = visibleProperties.find(p => p.id === id);
    if (property) {
      setSelectedId(id);
      
      // Get saved zoom from localStorage (NOT forced to 14)
      let restoreZoom = viewport.zoom || 5;
      try {
        const savedState = JSON.parse(localStorage.getItem('auctionmap') || 'null');
        if (savedState?.zoom) {
          restoreZoom = savedState.zoom;
        }
      } catch {
        // ignore
      }
      
      setFlyTarget({
        lat: property.lat,
        lng: property.lng,
        zoom: restoreZoom, // Use saved zoom, NOT 14
        key: Date.now()
      });
      
      // Scroll to card in slider
      setTimeout(() => {
        if (sliderRef.current) {
          const index = sliderRef.current.getCardIndex?.(id);
          if (index !== undefined && index !== -1) {
            sliderRef.current.scrollToCard(index);
          }
        }
      }, 300);
    }
  }
}, [visibleProperties]);

  useEffect(() => {
    let timer;
  
    const clearData = () => {
      const hasState = localStorage.getItem('auctionmap');
      if (!hasState) return;

      try {
        const state = JSON.parse(hasState);
        if (state.timestamp && Date.now() - state.timestamp > 60 * 60 * 1000) {
          localStorage.removeItem('auctionmap');
          // ✅ DON'T remove lastViewedPropertyId
          // localStorage.removeItem('lastViewedPropertyId');
          
          setSelectedLocation(null);
          setFlyTarget({ lat: 20.5937, lng: 78.9629, zoom: 5, key: Date.now() });
        }
      } catch {
        localStorage.removeItem('auctionmap');
      }
    };
  
    const startTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(clearData, 60 * 60 * 1000);
    };
  
    startTimer();
  
    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      startTimer();
    };
  
    window.addEventListener('click', resetTimer);
    window.addEventListener('scroll', resetTimer);
    window.addEventListener('keypress', resetTimer);
  
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('scroll', resetTimer);
      window.removeEventListener('keypress', resetTimer);
    };
  }, []);

  return (
    <>
      <CssBaseline />
      <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', bgcolor: 'transparent' }}>
        <MapView
          onViewportChange={handleViewportChange}
          onPropertiesUpdate={handlePropertiesUpdate}
          filters={filters}
          selectedId={selectedId}
          onMarkerClick={handleMarkerClick}
          flyTarget={flyTarget}
          getMapProperties={getAuctionMapProperties}
          categoryColorMap={categoryColorMap}
        />
        <TopToolbarNew
          typeOptions={mainCategoryOptions}
          propertyTypeOptions={subCategoryOptions}
          selectedTypes={selectedMainCategories}
          onTypesChange={setSelectedMainCategories}
          selectedPropertyTypes={selectedSubCategories}
          onPropertyTypesChange={setSelectedSubCategories}
          priceRange={priceRange}
          onPriceRangeChange={handlePriceRangeChange}
          onLocationSelect={handleLocationSelect}
          onClearLocation={handleClearLocation}
          selectedLocation={selectedLocation}
        />
        <PropertySlider
          ref={sliderRef}
          properties={visibleProperties}
      selectedId={selectedId} 
          onSelect={handleCardSelect}
          onView={setModalProperty}
          onCardInView={handleCardInView}
          showAlert={showAlert}
        />
        <PropertyModal property={modalProperty} onClose={() => setModalProperty(null)} />
      </Box>
      <FormsBottomNavbar />
    </>
  );
};

export default AuctionIndex;